/**
 * HTTP client con autenticación.
 *
 * - Adjunta el access_token a cada request si está disponible.
 * - Si una respuesta es 401, intenta refresh UNA vez. Si funciona, reintenta
 *   la request original. Si falla, hace logout.
 * - Lanza ApiError con status + payload en respuestas no-OK.
 */
import {
  clearPortalTokens,
  clearTokens,
  getPortalTokens,
  getTokens,
  setTokens,
} from "../auth/tokens";

/**
 * El portal del paciente lleva su propia sesión, guardada aparte.
 *
 * Se decide por la ruta y no por un parámetro para que ningún sitio pueda
 * equivocarse: todo lo que cuelga de /v1/portal usa el token del paciente, y
 * el resto el del personal.
 */
function esPortal(path: string): boolean {
  return path.startsWith("/v1/portal");
}
import { translateValidationErrors } from "./errors";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    public payload: unknown,
    message?: string,
  ) {
    super(message ?? `HTTP ${status}`);
  }
}

/**
 * Devuelve el mensaje de error legible (en español) de un ApiError.
 *
 * FastAPI/Pydantic devuelve `detail` como string en errores de negocio,
 * pero como array de objetos `{loc, msg, type, ctx}` en errores 422 de validación.
 * Renderizar el array crudo en JSX explota con "Objects are not valid as a React child".
 */
export function formatApiError(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  const detail = (err.payload as { detail?: unknown })?.detail;
  if (typeof detail === "string") return detail;
  const translated = translateValidationErrors(detail);
  if (translated) return translated;
  return fallback;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  auth?: boolean; // default true; pon false para endpoints públicos (login, refresh)
}

let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  const tokens = getTokens();
  if (!tokens?.refresh_token) return false;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: tokens.refresh_token }),
      });
      if (!response.ok) return false;
      const data = await response.json();
      setTokens({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        access_expires_at: data.access_expires_at,
        refresh_expires_at: data.refresh_expires_at,
      });
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, headers = {}, auth = true } = options;

  const buildRequest = (): RequestInit => {
    const reqHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      ...headers,
    };
    if (auth) {
      const tokens = esPortal(path) ? getPortalTokens() : getTokens();
      if (tokens?.access_token) {
        reqHeaders.Authorization = `Bearer ${tokens.access_token}`;
      }
    }
    return {
      method,
      headers: reqHeaders,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    };
  };

  let response = await fetch(`${API_BASE_URL}${path}`, buildRequest());

  // 401 → intenta refresh y reintenta una vez
  if (response.status === 401 && auth) {
    // La sesión del paciente no se renueva contra /auth/refresh —ese refresh es
    // del personal—, así que un 401 en el portal es sencillamente volver a
    // entrar, sin arrastrar al usuario del staff fuera de su sesión.
    if (esPortal(path)) {
      clearPortalTokens();
      const base = window.location.pathname.split("/portal")[0];
      if (!window.location.pathname.endsWith("/portal/entrar")) {
        window.location.assign(`${base}/portal/entrar`);
      }
    } else {
      const refreshed = await tryRefresh();
      if (refreshed) {
        response = await fetch(`${API_BASE_URL}${path}`, buildRequest());
      } else {
        clearTokens();
        // Hard redirect para limpiar todo el estado en memoria
        if (window.location.pathname !== "/login") {
          window.location.assign("/login");
        }
      }
    }
  }

  if (!response.ok) {
    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      // ignore
    }
    throw new ApiError(response.status, payload, `HTTP ${response.status}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/** Fetch crudo que también expone los headers (para detectar X-MFA-Required en login). */
export async function apiFetchRaw(
  path: string,
  options: RequestOptions = {},
): Promise<Response> {
  const { method = "GET", body, headers = {}, auth = true } = options;
  const reqHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    ...headers,
  };
  if (auth) {
    const tokens = getTokens();
    if (tokens?.access_token) {
      reqHeaders.Authorization = `Bearer ${tokens.access_token}`;
    }
  }
  return fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: reqHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}
